import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import join from 'lodash/join';
import isEmpty from 'lodash/isEmpty';

import { FlagMessageModal } from './flag-message-modal';
import { useStreamChatContext } from '../../../../utils/utils';
import { FlagMessageContextProvider } from './utils/flag-message-context';
import { injectIntl } from 'react-intl';
import { useToast } from '@ps-design-system/components/Toast';

const FlagMessageContainer = ({
  intl,
  message,
  children
}) => {
  const [showFlaggingModal, setShowFlaggingModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [userComment, setUserComment] = useState(null);
  const [hasFlagged, setHasFlagged] = useState(null);
  const [currentStep, setCurrentStep] = useState('reason');

  const { toast } = useToast();

  useEffect(() => {
    if (!showFlaggingModal) {
      setSelectedReason(null);
      setSelectedCategory(null);
      setUserComment(null);
      setCurrentStep('reason');
    }
  }, [showFlaggingModal]);

  const {
    client
  } = useStreamChatContext();

  const generateFlagPayload = useCallback(() => {
    const selectedReasonValue = selectedReason?.value;
    const selectedCategoryValue = selectedCategory?.value;

    const parts = [];

    if (selectedReasonValue) {
      parts.push(selectedReasonValue);
    }

    if (userComment) {
      parts.push(`Comment: ${userComment}`);
    } else if (selectedCategoryValue) {
      parts.push(`Comment: ${selectedCategoryValue}`);
    }

    const payload = {
      reason: join(parts, ' / '),
      custom: {
        reason: selectedReason?.value
      }
    };

    if (userComment || selectedCategory?.value) {
      payload.custom.user_comment = userComment || selectedCategory?.value;
    }

    return payload;
  }, [selectedReason, selectedCategory, userComment]);

  const flagMessage = useCallback(() => {
    // Avoid spamming
    if (hasFlagged) {
      setShowFlaggingModal(false);
      return;
    }
    const { id } = message;
    const payload = generateFlagPayload();
    client.flagMessage(id, payload);
    setShowFlaggingModal(false);
    setHasFlagged(true);

    toast({
      title: '',
      description: intl.formatMessage({
        id: 'stream.flagging_success_banner.message',
        defaultMessage: 'Thank you for your report. We review all reports thoroughly and take action in accordance with our community guidelines.'
      }),
      variant: 'success',
      duration: 5000
    });

  }, [message, client, generateFlagPayload, hasFlagged]);

  const goBack = useCallback(() => {
    const hasCategoryFlow = selectedReason && !isEmpty(selectedReason.categories);
    if ('category' === currentStep) {
      setCurrentStep('reason');
    } else if ('details' === currentStep && hasCategoryFlow) {
      setCurrentStep('category');
    } else {
      setCurrentStep('reason');
    }
  }, [currentStep, selectedReason, selectedCategory]);

  const handleShowFlaggingModal = () => {
    setShowFlaggingModal(true);
  };

  return (
    <FlagMessageContextProvider
      showModal={showFlaggingModal}
      setShowModal={setShowFlaggingModal}
      flagMessage={flagMessage}
      setSelectedReason={setSelectedReason}
      setSelectedCategory={setSelectedCategory}
      setUserComment={setUserComment}
      selectedReason={selectedReason}
      selectedCategory={selectedCategory}
      userComment={userComment}
      setCurrentStep={setCurrentStep}
      currentStep={currentStep}
      goBack={goBack}
    >
      {children({ showFlaggingModal: handleShowFlaggingModal })}
      <FlagMessageModal/>
    </FlagMessageContextProvider>
  );
};

FlagMessageContainer.propTypes = {
  intl: PropTypes.object,
  message: PropTypes.object,
  children: PropTypes.node
};

export default injectIntl(FlagMessageContainer);
